import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { AuthUser } from '@/features/auth/types';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  setAuth: (accessToken: string, refreshToken: string, user?: AuthUser) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    set => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      setAuth: (accessToken, refreshToken, user) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);

        const enrichedUser = user
          ? {
              ...user,
              fullName:
                [user.firstName, user.lastName].filter(Boolean).join(' ') ||
                user.login,
            }
          : null;

        set({
          accessToken,
          refreshToken,
          user: enrichedUser,
          isAuthenticated: true,
        });
      },
      setTokens: (accessToken, refreshToken) => {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        set({ accessToken, refreshToken });
      },
      setUser: user => {
        const enrichedUser = {
          ...user,
          fullName:
            [user.firstName, user.lastName].filter(Boolean).join(' ') ||
            user.login,
        };
        set({ user: enrichedUser });
      },
      logout: () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
